import React, {
  FC,
  lazy,
  memo,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useSetFocus } from '@please/lrud'
import { useNavigate } from 'react-router-dom'

import { toggleAppSettings } from '@/actions/actions'
import { Button } from '@/components/Button/Button'
import { focusIds, orientation } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { popupStatus } from '@/constants/popup'
import { scrollOptions } from '@/constants/scrollers'
import { Search } from '@/containers/Search/Search'
import { Container, ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useIsBroadcastVisible } from '@/hooks/useIsBroadcastVisible'
import { usePageOptionsUpdate } from '@/hooks/usePageOptionsUpdate'
import useStore from '@/store/useStore'
import { HomeTab, HomeTabTypes } from '@/types/servicesTypes/configTypes'
import { LinesGroupType, scrollOptionsType } from '@/types/sliderTypes'

import { HomeTabPageHeader } from './components/HomeTabPageHeader'
import { HomeTabPageLines } from './components/HomeTabPageLines'
import { HomePageStage } from './components/HomeTabPageStage'
import { HomePageTabs } from './components/HomeTabPageTabs'
import { ContentWrapper, HomeTabPageMainLayout } from './styles'

const LinesGroup: FC<LinesGroupType> = lazy(
  () => import('@/components/LinesGroup/LinesGroup'),
)

const HomeTabPageRaw: FC<{
  id: string
  homeTab: HomeTab[]
  focusIdPrefix?: string
  tabIndex?: number
  slideIndex?: number
  initFocusId?: string
  anchorId?: string
  isPopup?: boolean
  scrollOptions: scrollOptionsType
}> = ({
  id,
  homeTab,
  focusIdPrefix = '',
  tabIndex,
  slideIndex,
  initFocusId,
  anchorId,
  isPopup,
}) => {
  const containerFocusId = `${focusIdPrefix}${focusIds.CONTAINER_MOD}_${id}`
  const mainLayoutFocusId = `${focusIdPrefix}${focusIds.CONTAINER_MOD}_homePageMainLayout`

  const isFullscreen = useStore((state) => state.player.isFullscreen)
  const navigate = useNavigate()
  const setFocus = useSetFocus()
  const appSettings = useStore((state) => state.common.appSettings)

  const { setTabIndex } = usePageOptionsUpdate(isPopup)

  const [currentTabIndex, setCurrentTabIndex] = useState(tabIndex ?? 0)
  const [defaultSlideIndex, setDefaultSlideIndex] = useState(slideIndex ?? 0)

  const homePageTabsData = homeTab

  const currentTabData = homePageTabsData[currentTabIndex]
  const isStagePageType =
    currentTabData?.type == HomeTabTypes.STAGE_LINES ||
    currentTabData?.type == HomeTabTypes.STAGE_SECTION_GRID

  const stageData = isStagePageType
    ? currentTabData?.stageLinesPage?.stage
    : undefined

  const isSearchPageType = currentTabData?.type == HomeTabTypes.SEARCH

  /**
   * Fast solution to support HomePage type: 'StageLinesPage' | 'Lines'
   */
  const memoizedLines = useMemo(
    () => currentTabData?.stageLinesPage?.lines || currentTabData?.lines || [],
    [currentTabData],
  )

  const pagePopupStatus = useStore((state) => state.popup.pagePopupStatus)
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED
  const listPopup = useStore((state) => state.common.listPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const hasChildPopup = Boolean(listPopup || currentEvent)
  const isPageContentVisible = (isPopup || !isPagePopupOpen) && !hasChildPopup

  const { defaultFocusChild: containerFocusChild, onMove: onContainerMove } =
    useCustomFocus({
      focusId: containerFocusId,
      scrollOptions: scrollOptions.HomeScroll,
      hasNestedScroll: !!memoizedLines.length,
      startFocusChild: initFocusId || anchorId ? 1 : undefined,
    })

  const { defaultFocusChild: mainLayoutFocusChild, onMove: onMainLayoutMove } =
    useCustomFocus({
      startFocusChild: 1,
      focusId: mainLayoutFocusId,
      // scrollOptions: scrollOptions.HomeScroll,
      resetStartFocusChild: true,
      needSetFocus: isPageContentVisible,
    })

  useEffect(() => {
    setTabIndex(currentTabIndex)
  }, [currentTabIndex])

  useEffect(() => {
    if (!slideIndex) {
      return setDefaultSlideIndex(0)
    }
  }, [slideIndex])

  useEffect(() => {
    isPageContentVisible && setFocus(containerFocusId)
  }, [isPageContentVisible])

  const navigateLogoutPage = () => navigate(paths.LOGOUT)

  const { recalculate } = useIsBroadcastVisible()

  return (
    <Container
      elementType="div"
      defaultFocusChild={containerFocusChild}
      focusId={containerFocusId}
      orientation={orientation.VERTICAL}
      onMove={onContainerMove}
      onBack={navigateLogoutPage}
    >
      {!!homePageTabsData.length && (
        <>
          <HomeTabPageMainLayout
            defaultFocusChild={mainLayoutFocusChild}
            focusId={mainLayoutFocusId}
            orientation="vertical"
            onMove={onMainLayoutMove}
          >
            <ContentWrapper>
              <HomeTabPageHeader
                isHidden={isFullscreen}
                focusIdPrefix={focusIdPrefix}
              >
                <HomePageTabs
                  items={homePageTabsData}
                  onSelected={setCurrentTabIndex}
                  defaultTabIndex={currentTabIndex}
                  isFocused={
                    mainLayoutFocusChild === 0 && containerFocusChild === 0
                  }
                  focusIdPrefix={focusIdPrefix}
                  isPopup={isPopup}
                />
              </HomeTabPageHeader>
            </ContentWrapper>
            <HomePageStage
              stageData={stageData}
              isVisible={containerFocusChild === 0}
              id={focusIdPrefix + currentTabData.id + 'stage'}
              currentTabIndex={currentTabIndex}
              defaultSlideIndex={defaultSlideIndex}
              isPopup={isPopup}
              focusIdPrefix={focusIdPrefix}
            />
          </HomeTabPageMainLayout>

          {!!memoizedLines.length && (
            <HomeTabPageLines
              isHidden={isFullscreen}
              isVisible={!stageData?.slides.length || containerFocusChild === 1}
            >
              <ScrollContainer
                onScroll={recalculate}
                id={scrollOptions.HomeScrollWithStage.containerId}
                height={scrollOptions.HomeScrollWithStage.height}
                $withMargin
              >
                {!appSettings.isDisabledLines ? (
                  <Suspense>
                    <LinesGroup
                      groupData={memoizedLines}
                      scrollOptions={scrollOptions.HomeScrollWithStage}
                      focusIdPrefix={focusIdPrefix}
                      initFocusId={initFocusId}
                      anchorId={anchorId}
                      isNested={true}
                      resetNestedFocusChild={!containerFocusChild}
                      isPopup={isPopup}
                    />
                  </Suspense>
                ) : (
                  <Button
                    focusId={`${focusIdPrefix}_HomeTabPageEnableLinesButton`}
                    text="Enable Lines"
                    onClick={() => {
                      toggleAppSettings({ isDisabledLines: false })
                    }}
                  />
                )}
              </ScrollContainer>
            </HomeTabPageLines>
          )}
          {isSearchPageType && (
            <HomeTabPageLines isHidden={false} isVisible={isSearchPageType}>
              <ScrollContainer
                onScroll={recalculate}
                id={scrollOptions.HomeScrollWithStage.containerId}
                height={scrollOptions.HomeScrollWithStage.height}
                $withMargin
              >
                {isSearchPageType && <Search isSearchTab />}
              </ScrollContainer>
            </HomeTabPageLines>
          )}
        </>
      )}
    </Container>
  )
}

export const HomeTabPage = memo(HomeTabPageRaw)
